import React from 'react';
import PropTypes from 'prop-types';
import EmojiConfetti from '../EmojiConfetti';
import {
  groupAssignmentsByWeek,
  getCurrentWeek,
} from '../modals/IndividualStudentGradebook/utils';
import { isTabToolLaunch } from '@macmillan-learning/gradebook-shared';
import WeeklyAssignmentsDisplay from './WeeklyAssignmentsGraph';
import {
  sortWeeklyAssignmentsByStatus,
  getNextAssignmentDueString,
  getMotivationalMessage,
} from './utils';
import { WEEKLY_PROGRESS_MESSAGES } from '../../utils/constants';
import s from './WeeklyProgress.module.css';

const UNSUPPORTED_TOOLS = ['manual', 'reef'];

const WeeklyProgress = ({
  course,
  assignments,
  settings,
  getLaunchUrl,
  handleLocalLaunch,
}) => {
  if (!course) {
    return null;
  }

  const { enrollmentStartDate, startWeekOn } = course;

  const filteredAssignments = assignments.filter(
    (assignment) => !UNSUPPORTED_TOOLS.includes(assignment.tool),
  );
  const filteredAssignmentIds = filteredAssignments.map((a) => a.id);

  const assignmentsByWeek = groupAssignmentsByWeek(
    assignments,
    filteredAssignmentIds,
    enrollmentStartDate,
    startWeekOn,
    settings,
  );

  let sortedCurrentWeekAssignments = [];
  const currentWeek = getCurrentWeek(assignmentsByWeek);

  if (currentWeek) {
    const currentWeekData = assignmentsByWeek.find(
      (week) => week.id === currentWeek,
    );
    const currentWeekAssignments = currentWeekData?.assignments || [];
    sortedCurrentWeekAssignments = sortWeeklyAssignmentsByStatus(
      currentWeekAssignments,
    );
  }

  const daysUntilDueString = getNextAssignmentDueString(filteredAssignments);
  const motivationalMessage = getMotivationalMessage(
    sortedCurrentWeekAssignments,
  );
  const weekIsCompleted =
    motivationalMessage === WEEKLY_PROGRESS_MESSAGES.ALL_COMPLETED;

  const handleAssignmentClick = (event, assignment) => {
    event.preventDefault();
    const launchUrl = getLaunchUrl({
      ...assignment,
      actual_course_id: assignment.course_id,
    });
    const isNewTabTool = isTabToolLaunch(assignment.tool);
    const openInNewTab =
      isNewTabTool || event.ctrlKey || event.shiftKey || event.metaKey;
    handleLocalLaunch(launchUrl, openInNewTab);
  };

  const renderNullState = () => {
    return (
      <div className={s.noAssignments}>
        No assignments due this week!
        <br />
        You can get started on next week or review previous assignments to keep
        up your learning
      </div>
    );
  };

  return (
    <div className={s.weeklyProgressWrapper}>
      <div className={s.weeklyProgressCard}>
        <span
          className={s.weeklyProgressTitle}
          data-test-id="studentGB_progress_bar_header"
        >
          Week {currentWeek} Progress
        </span>
        {weekIsCompleted && <EmojiConfetti />}
        <div className={s.subheader}>
          <span>{daysUntilDueString}</span>
          <span>{motivationalMessage}</span>
        </div>
        {sortedCurrentWeekAssignments.length > 0 && (
          <WeeklyAssignmentsDisplay
            assignments={sortedCurrentWeekAssignments}
            onAssignmentClick={handleAssignmentClick}
            weekIsCompleted={weekIsCompleted}
          />
        )}
        {sortedCurrentWeekAssignments.length === 0 && renderNullState()}
      </div>
    </div>
  );
};

WeeklyProgress.propTypes = {
  course: PropTypes.object.isRequired,
  assignments: PropTypes.array.isRequired,
  settings: PropTypes.array.isRequired,
  getLaunchUrl: PropTypes.func.isRequired,
  handleLocalLaunch: PropTypes.func.isRequired,
};

export default WeeklyProgress;
